// How a character sounds: the settings for whichever "Who does the voice?" tab is picked on the voice page (you record
// the lines, or an AI voice says what you type). "Try it" plays a sample back; while it plays, `onMouth` gets the mouth shape for the preview each frame.
// The AI voice screen: a starting voice you can hear instantly (narrowed to men's or women's voices), quick picks that
// write the first sentence of the "how they talk" text. Age, accent and personality also take your own words. "How they talk" shows that sentence as it
// is now (Clear drops the picks) with a box under it for anything else; the two together are what the voice is told.
import { useEffect, useRef, useState } from "react";
import { lipSyncUrl } from "../../audio/lipsync";
import { MicRecorder, engine } from "../../audio/playback";
import { NATURAL, VOICE_EFFECTS, VOICE_PRESETS, presetOf, type VoiceChain } from "../../audio/voice/voice";
import { FPS } from "../../engine/timeline";
import { visemeFrames } from "../../engine/visemes";
import type { Asset } from "../../model/library";
import type { Cue, Viseme } from "../../model/face";
import { ACCENTS, AGES, AGE_NAMES, BASE_VOICES, DEFAULT_SPEECH, MANNERS, MAX_LINE, MAX_MANNERS, MAX_OWN_PICK, MAX_STYLE, SPEEDS, SPEED_NAMES, baseVoiceOf, composeStyle, describePicks, extraOf, isOffered, type Gender, type GeminiVoice, type Picks, type SpeechVoice } from "../../model/speech";
import type { VoiceMode, VoiceSettings } from "../../model/voice";
import { api } from "../api";
import { Icon, Section, Slider } from "../common";

type Sample = { url: string; duration: number; cues: Cue[]; kind: VoiceMode };
type Rec = { phase: "idle" } | { phase: "recording"; start: number } | { phase: "processing" } | { phase: "generating" };
type Heard = { id: number; voice: string; text: string; sample: Sample };
const MAX_SAMPLE = 5;
let heardList: Heard[] = []; // AI voices heard this session, so you can compare them before picking
let lastSample: Sample | null = null; // kept for the session, so every visit can reuse it
const pct = (v: number) => (Math.abs(v) < 0.01 ? "0" : `${v > 0 ? "+" : ""}${Math.round(v * 100)}`);

// The picked option's settings.
export function VoiceSetup({ draft, onChange, onMouth, onToast }: {
  draft: Asset; onChange: (patch: Partial<Asset>) => void; onMouth: (m: Viseme | null) => void; onToast: (m: string) => void;
}) {
  const voice = draft.voice ?? NATURAL, speech = draft.speech ?? DEFAULT_SPEECH;
  const ai = draft.voiceMode === "ai";
  const who = draft.name.trim() || "they";
  const setVoice = (v: Partial<VoiceSettings>) => onChange({ voice: { ...voice, ...v } });
  const setSpeech = (s: Partial<SpeechVoice>) => onChange({ speech: { ...speech, ...s } });
  const [sampleText, setSampleText] = useState("Has anyone seen the milk? I only bought it this morning.");
  const [sample, setSample] = useState<Sample | null>(lastSample);
  const [playing, setPlaying] = useState(false);
  const [heard, setHeard] = useState(heardList);
  const [heardPlaying, setHeardPlaying] = useState<number | null>(null);
  const [previewing, setPreviewing] = useState<GeminiVoice | null>(null);
  const previews = useRef(new Map<GeminiVoice, Sample>());
  const [rec, setRec] = useState<Rec>({ phase: "idle" });
  const [levels, setLevels] = useState<number[]>(Array(12).fill(0));
  const [elapsed, setElapsed] = useState(0);
  const chain = useRef<VoiceChain | null>(null);
  const gen = useRef(0);
  const mic = useRef(new MicRecorder());
  const filter = ai ? NATURAL : voice; // filters only apply to recorded lines
  useEffect(() => { chain.current?.update(filter); }, [filter]);

  const stop = () => { gen.current++; chain.current = null; engine.stop(); setPlaying(false); setHeardPlaying(null); setPreviewing(null); onMouth(null); };
  async function play(s = sample, v = filter, id: number | null = null, pv: GeminiVoice | null = null) {
    if (!s) return;
    const my = ++gen.current;
    const buf = await engine.load(s.url);
    const { chain: c, clock } = await engine.playSample(buf, v);
    if (my !== gen.current) return;
    chain.current = c;
    setPlaying(true);
    setHeardPlaying(id);
    setPreviewing(pv);
    const seq = visemeFrames(s.cues, s.duration, FPS);
    const loop = () => {
      if (my !== gen.current) return;
      const t = clock();
      if (t > s.duration + 0.15) { chain.current = null; setPlaying(false); setHeardPlaying(null); setPreviewing(null); onMouth(null); return; }
      onMouth(t >= 0 && t < s.duration ? seq[Math.floor(t * FPS)] ?? "X" : null);
      requestAnimationFrame(loop);
    };
    loop();
  }
  useEffect(() => () => { gen.current++; engine.stop(); }, []);

  // The 12 pre-recorded base voices play straight away, no waiting for the model.
  async function preview(v: GeminiVoice) {
    stop();
    let s = previews.current.get(v);
    if (!s) {
      const url = `/voice-previews/${v}.wav`;
      try { s = { url, duration: (await engine.load(url)).duration, cues: await lipSyncUrl(url), kind: "ai" }; } catch { return onToast("Couldn't load that voice sample."); }
      previews.current.set(v, s);
    }
    void play(s, NATURAL, null, v);
  }
  const pickVoice = (v: GeminiVoice) => { setSpeech({ voice: v }); void preview(v); };
  // Picks write the first sentence; whatever was typed after it stays.
  const extra = extraOf(speech);
  const setPicks = (patch: Partial<Picks> | null) => {
    const next: Picks = patch ? { ...speech.picks, ...patch } : {};
    const g = baseVoiceOf(speech.voice)?.gender;
    const pg = patch?.gender;
    const voicePatch = (pg === "male" || pg === "female") && g && g !== pg ? { voice: BASE_VOICES.find(b => b.gender === pg)!.voice } : {};
    setSpeech({ ...voicePatch, picks: next, style: composeStyle(next, extra) });
  };
  const togglePick = (k: "age" | "accent" | "speed", v: string) => setPicks({ [k]: speech.picks?.[k] === v ? undefined : v });
  const toggleGender = (g: Gender) => setPicks({ gender: speech.picks?.gender === g ? undefined : g });
  const manner = speech.picks?.manner ?? [];
  const offeredManner = manner.filter(m => isOffered(MANNERS, m)), ownManner = manner.find(m => !isOffered(MANNERS, m)) ?? "";
  const toggleManner = (m: (typeof MANNERS)[number]) => {
    const cur = offeredManner;
    const next = cur.includes(m) ? cur.filter(x => x !== m) : cur.length >= MAX_MANNERS ? cur : [...cur, m];
    setPicks({ manner: ownManner ? [...next, ownManner] : next });
  };
  const setOwnManner = (v: string) => setPicks({ manner: v ? [...offeredManner, v] : offeredManner });
  const gender = speech.picks?.gender;
  const ownOf = (list: readonly string[], v: string | undefined) => (v && !list.includes(v) ? v : "");
  const head = describePicks(speech.picks);

  async function startRec() {
    stop();
    try { await mic.current.open(); } catch { return onToast("Microphone blocked. Allow mic access for this page and try again."); }
    mic.current.start();
    setElapsed(0);
    setRec({ phase: "recording", start: performance.now() });
  }
  async function stopRec() {
    if (rec.phase !== "recording") return;
    const secs = (performance.now() - rec.start) / 1000;
    setRec({ phase: "processing" });
    const blob = await mic.current.stop();
    if (blob.size < 500 || secs < 0.6) { setRec({ phase: "idle" }); return onToast("That was too short. Say a sentence or two."); }
    try {
      const r = await api.voiceSample(blob);
      const s: Sample = { url: r.url, duration: r.duration, cues: await lipSyncUrl(r.url), kind: "record" };
      lastSample = s;
      setSample(s);
      setRec({ phase: "idle" });
      void play(s);
    } catch (e) { setRec({ phase: "idle" }); onToast((e as Error).message); }
  }
  async function hear() {
    if (!sampleText.trim()) return onToast("Type something for them to say.");
    stop();
    setRec({ phase: "generating" });
    try {
      const r = await api.speakSample({ text: sampleText, speech, emotion: "neutral" });
      const s: Sample = { url: r.url, duration: r.duration, cues: await lipSyncUrl(r.url), kind: "ai" };
      const words = speech.style.split(/\s+/).filter(Boolean).slice(0, 4).join(" ");
      const h: Heard = { id: Date.now(), voice: `${baseVoiceOf(speech.voice)?.name ?? speech.voice}${words ? ` · ${words}…` : ""}`, text: sampleText, sample: s };
      heardList = [h, ...heardList].slice(0, 8);
      setHeard(heardList);
      setSample(s);
      setRec({ phase: "idle" });
      void play(s, NATURAL, h.id);
    } catch (e) { setRec({ phase: "idle" }); onToast((e as Error).message); }
  }
  useEffect(() => {
    if (rec.phase !== "recording") return;
    let raf = 0, last = 0;
    const loop = (now: number) => {
      if (now - last > 50) {
        last = now;
        setLevels(v => [...v.slice(1), mic.current.level()]);
        const e = (now - rec.start) / 1000;
        setElapsed(e);
        if (e >= MAX_SAMPLE) { void stopRec(); return; }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [rec]);

  const preset = presetOf(voice);
  const pickPreset = (v: VoiceSettings) => { setVoice(v); if (sample?.kind === "record" && rec.phase === "idle") void play(sample, v); };

  return (
    <>
      {ai ? (
        <>
          <Section title="Starting voice" hint="Pick man or woman to narrow the list. Tap a voice to hear it.">
            <div className="chips" role="group" aria-label="Man or woman">
              {(["male", "female"] as Gender[]).map(g => <button key={g} aria-pressed={gender === g} className={`chip${gender === g ? " on" : ""}`} onClick={() => toggleGender(g)}>{g === "male" ? "Man" : "Woman"}</button>)}
            </div>
            <div className="rows vp-voices">
              {!baseVoiceOf(speech.voice) && (
                <div className="row-link" aria-current="true">
                  <span className="row-text"><b>{speech.voice} (older voice)</b></span>
                  <button className="btn" aria-label={`${previewing === speech.voice ? "Stop" : "Play"} ${speech.voice}`} onClick={() => (previewing === speech.voice ? stop() : void preview(speech.voice))}><Icon name={previewing === speech.voice ? "stop" : "play"} size={16} /></button>
                </div>
              )}
              {BASE_VOICES.filter(b => !gender || b.gender === gender).map(b => (
                <div key={b.voice} className="row-link" aria-current={speech.voice === b.voice} onClick={() => pickVoice(b.voice)}>
                  <span className="row-text"><b>{b.name}</b>{!gender && <span>{b.gender === "male" ? "Man" : "Woman"}</span>}</span>
                  <button className="btn" aria-label={`${previewing === b.voice ? "Stop" : "Play"} ${b.name}`} onClick={e => { e.stopPropagation(); if (previewing === b.voice) stop(); else void preview(b.voice); }}>
                    <Icon name={previewing === b.voice ? "stop" : "play"} size={16} />
                  </button>
                </div>
              ))}
            </div>
          </Section>
          <Section title="Age">
            <div className="chips">{AGES.map(a => <button key={a} className={`chip${speech.picks?.age === a ? " on" : ""}`} onClick={() => togglePick("age", a)}>{AGE_NAMES[a]}</button>)}</div>
            <OwnPick label="Or say their age" placeholder="Or your own, e.g. middle-aged, ancient" value={ownOf(AGES, speech.picks?.age)} onChange={v => setPicks({ age: v || undefined })} />
          </Section>
          <Section title="Accent">
            <div className="chips">{ACCENTS.map(a => <button key={a} className={`chip${(speech.picks?.accent ?? "none") === a ? " on" : ""}`} onClick={() => setPicks({ accent: a === "none" ? undefined : a })}>{a === "none" ? "None" : a}</button>)}</div>
            <OwnPick label="Or say their accent" placeholder="Or your own, e.g. South African, Welsh" value={ownOf(ACCENTS, speech.picks?.accent)} onChange={v => setPicks({ accent: v || undefined })} />
          </Section>
          <Section title="Personality" hint={`Pick up to ${MAX_MANNERS}, and add your own.`}>
            <div className="chips">{MANNERS.map(m => <button key={m} className={`chip${offeredManner.includes(m) ? " on" : ""}`} onClick={() => toggleManner(m)}>{m[0]!.toUpperCase() + m.slice(1)}</button>)}</div>
            <OwnPick label="Or say their personality" placeholder="Or your own, e.g. sly, sarcastic" value={ownManner} onChange={setOwnManner} />
          </Section>
          <Section title="Speed" hint="How fast they talk. Leave it unpicked for their natural pace.">
            <div className="chips">{SPEEDS.map(v => <button key={v} className={`chip${speech.picks?.speed === v ? " on" : ""}`} onClick={() => togglePick("speed", v)}>{SPEED_NAMES[v]}</button>)}</div>
            <OwnPick label="Or say how they talk" placeholder="Or your own, e.g. in short bursts, like an auctioneer" value={ownOf(SPEEDS, speech.picks?.speed)} onChange={v => setPicks({ speed: v || undefined })} />
          </Section>
          <Section title="How they talk" hint="This is everything the voice is told about them.">
            <div className="vp-head">
              {head
                ? <><p><span className="vp-head-label">From your picks</span>{head}</p><button className="btn small" onClick={() => setPicks(null)}><Icon name="close" size={16} /> Clear</button></>
                : <p className="hint">Nothing picked above, so only the words below are used.</p>}
            </div>
            <textarea className="input" rows={3} maxLength={Math.max(0, MAX_STYLE - (head ? head.length + 1 : 0))} value={extra} aria-label="Anything else about how they talk"
              placeholder={head ? "Anything else, e.g. low and dry, never raises his voice" : "e.g. A weary detective, low and dry, never raises his voice"} onChange={e => setSpeech({ style: composeStyle(speech.picks, e.target.value) })} />
          </Section>
          <Section title="Hear it">
            <div className="try">
              <input className="input" value={sampleText} maxLength={MAX_LINE} aria-label="Something for them to say" onChange={e => setSampleText(e.target.value)} />
              <button className="btn yellow" disabled={rec.phase !== "idle"} onClick={() => void hear()}>
                {rec.phase === "generating" ? <><span className="spinner" /> Making it…</> : <><Icon name="play" size={18} /> Hear {who}</>}
              </button>
            </div>
          </Section>
          {heard.length > 0 && (
            <Section title="Voices you've heard">
              <div className="rows">
                {heard.map(h => (
                  <div key={h.id} className="row-link">
                    <span className="row-text"><b>{h.voice}</b><span>{h.text}</span></span>
                    <button className="btn" aria-label={`${heardPlaying === h.id ? "Stop" : "Play"} ${h.voice}`} onClick={() => (heardPlaying === h.id ? stop() : void play(h.sample, NATURAL, h.id))}>
                      <Icon name={heardPlaying === h.id ? "stop" : "play"} size={16} />
                    </button>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </>
      ) : (
        <>
          <Section title="Try it" hint={`Say a sentence and hear it back the way ${who} will sound.`}>
            <div className="try">
              {rec.phase === "recording" ? (
                <>
                  <div className="levels">{levels.map((l, i) => <span key={i} style={{ height: `${8 + l * 92}%` }} />)}</div>
                  <button className="btn" onClick={() => void stopRec()}><Icon name="stop" size={16} /> Stop · {elapsed.toFixed(1)}s</button>
                </>
              ) : rec.phase === "processing" ? (
                <div className="busy-line"><span className="spinner" /> Getting the mouth ready…</div>
              ) : sample?.kind === "record" ? (
                <>
                  <button className="btn yellow" onClick={() => (playing ? stop() : void play())}>{playing ? <><Icon name="stop" size={16} /> Stop</> : <><Icon name="play" size={18} /> Play it back</>}</button>
                  <button className="btn" onClick={() => void startRec()}><Icon name="mic" size={18} /> Record again</button>
                </>
              ) : (
                <button className="btn yellow" onClick={() => void startRec()}><Icon name="mic" size={18} /> Record a sentence</button>
              )}
            </div>
          </Section>
          <Section title="Pick a filter">
            <div className="chips">
              {VOICE_PRESETS.map(p => <button key={p.id} className={`chip${preset === p.id ? " on" : ""}`} onClick={() => pickPreset(p.voice)}>{p.name}</button>)}
              {preset === "custom" && <span className="chip on">Your own</span>}
            </div>
          </Section>
          <details className="fine">
            <summary>Fine-tune the filter</summary>
            <Slider label="Pitch" min={-12} max={12} step={0.5} value={voice.pitch} onChange={pitch => setVoice({ pitch })} left="Lower" right="Higher" show={v => `${v > 0 ? "+" : ""}${v}`} />
            <Slider label="Size" min={-1} max={1} step={0.05} value={voice.size} onChange={size => setVoice({ size })} left="Small" right="Big" show={pct} />
            <Slider label="Tone" min={-1} max={1} step={0.05} value={voice.tone} onChange={tone => setVoice({ tone })} left="Warm" right="Bright" show={pct} />
            <div className="field"><span>Effect</span>
              <div className="chips">{VOICE_EFFECTS.map(e => <button key={e.id} className={`chip${voice.effect === e.id ? " on" : ""}`} onClick={() => setVoice({ effect: e.id })}>{e.name}</button>)}</div>
            </div>
            <Slider label="Amount" min={0} max={1} step={0.05} value={voice.amount} disabled={voice.effect === "none"} onChange={amount => setVoice({ amount })} left="Subtle" right="Strong" show={v => `${Math.round(v * 100)}%`} />
          </details>
        </>
      )}
    </>
  );
}

// A pick in your own words, under a row of offered ones. Empty means none of your own.
function OwnPick({ label, placeholder, value, onChange }: { label: string; placeholder: string; value: string; onChange: (v: string) => void }) {
  return <input className="input own-pick" maxLength={MAX_OWN_PICK} value={value} aria-label={label} placeholder={placeholder} onChange={e => onChange(e.target.value)} />;
}
